/**
 * Transform API cadence data shapes into what UI components expect.
 *
 * --- NAMING GLOSSARY ---
 * The same concept often has two names depending on whether you're reading
 * backend code/GraphQL or frontend/UI code. This module bridges the gap.
 *
 * | Backend term   | Frontend/UI term | Notes                                          |
 * |----------------|------------------|------------------------------------------------|
 * | event          | step             | A single action node in the cadence graph      |
 * | edge           | connection       | Link between two events; has edgeType          |
 * | member         | recipient        | A contact enrolled in a cadence                |
 * | cadence_type   | type / typeLabel | 'cadence', 'prospect_plan', 'bulk_message'     |
 * | shell / template | blueprint      | A saved cadence used as a starting template    |
 * | automated=true | Automated step   | Email steps sent by Temporal; false = Manual   |
 * | sequence_order | step number      | Positional ordering; branches get suffix (3a)  |
 *
 * IMPORTANT: "cadence" is overloaded — it can mean:
 *   - Any cadence-type record (generic)
 *   - Specifically cadenceType === 'cadence' (not bulk_message, not prospect_plan)
 * Use cadenceType to disambiguate when the distinction matters.
 *
 * STEP NUMBERS: The backend now provides `stepLabel` (e.g., "3a") on events and
 * stats. This module prefers backend-provided labels over client-computed ones.
 * Client-side numbering via walkCadenceGraph is a fallback for older records
 * that predate backend step labeling.
 */
import { CADENCE_TYPE_LABELS } from './cadence-enums';
import { STEP_TYPE_LABELS as SHARED_LABELS, walkCadenceGraph, getTaskTypeInfo } from './cadence-step-utils';

// Re-export shared labels with uppercase aliases for backward compat
const EVENT_TYPE_LABELS = {
  ...SHARED_LABELS,
  EMAIL: 'Automated Email',
  MANUAL: 'Manual',
  TASK: 'Task',
  SMS: 'SMS',
  CALL: 'Call',
  WAIT: 'Wait',
  START: 'Start',
  END: 'End',
};

export const METRIC_TYPE_LABELS = {
  gift_given: 'Gift Given',
  interaction_created: 'Interaction Created',
  email_replied: 'Email Response Rate',
  email_opened: 'Email Open Rate',
  email_clicked: 'Email Click Rate',
  proposal_funded: 'Proposal Funded',
  gifts_amount: 'Gifts Over Amount',
  gifts_count: 'Number of Gifts',
  meetings_count: 'Number of Meetings',
  saved_search: 'Saved Search Qualification',
  portfolio_stage_reached: 'Portfolio Stage Reached',
  // Legacy keys — existing cadences may have these stored in the DB
  email_response_rate: 'Email Response Rate',
  email_open_rate: 'Email Open Rate',
  email_click_rate: 'Email Click Rate',
  bounced: 'Bounced Email',
};

/**
 * Format an ISO date string to a display date like "Oct 15, 2024".
 * Shared across cadences-v2 — import from this module instead of redefining.
 */

/** MySQL strips tz info; append Z so browsers parse naive strings as UTC, not local time. */
export const parseUtcDate = (s) => {
  if (!s) return null;
  if (s instanceof Date) return s;
  return new Date(!s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s) ? s + 'Z' : s);
};

export const formatDate = (isoString) => {
  if (!isoString) return null;
  try {
    // Date-only strings (YYYY-MM-DD) — parse as local midnight to avoid day shift.
    if (typeof isoString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(isoString)) {
      const [y, m, d] = isoString.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
    const date = parseUtcDate(isoString);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return null;
  }
};

/** Same as formatDate but returns '—' instead of null for empty/invalid values. */
export const formatDateOrDash = (isoString) => formatDate(isoString) || '\u2014';

/** Format an ISO date to "Jan 5" (month + day, no year). Returns null for empty/invalid. */
export const formatShortDate = (isoString) => {
  if (!isoString) return null;
  try {
    const d = isoString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoString) ? isoString : isoString + 'Z';
    const date = new Date(d);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch {
    return null;
  }
};

/** Format an ISO date to "01/05/2026" (MM/DD/YYYY). Returns '—' for empty/invalid. */
export const formatSlashDate = (isoString) => {
  if (!isoString) return '\u2014';
  try {
    const d = isoString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoString) ? isoString : isoString + 'Z';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '\u2014';
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  } catch {
    return '\u2014';
  }
};

/** Format a date in a specific IANA timezone (e.g., "America/New_York"). */
export const formatDateInTimezone = (isoString, timezone) => {
  if (!isoString) return null;
  if (!timezone) return formatDate(isoString);
  try {
    // Treat naive strings (no Z / offset) as UTC — the backend stores scheduled send
    // times in UTC as naive datetimes, so we must add Z before parsing.
    const utc = isoString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoString) ? isoString : isoString + 'Z';
    const date = new Date(utc);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
    });
  } catch {
    return formatDate(isoString);
  }
};

/** Event types that are internal workflow markers — never shown as "steps" to users. */
const INTERNAL_EVENT_TYPES = ['start', 'end'];
/** Event types hidden from detail page display (internal + non-actionable). */
const HIDDEN_STEP_TYPES = ['start', 'end', 'wait', 'condition'];

/**
 * Filter out start/end events and sort by sequenceOrder.
 * Used for step counts in list views.
 */
const getActionableEvents = (events = []) =>
  events
    .filter((e) => !INTERNAL_EVENT_TYPES.includes((e.eventType || '').toLowerCase()))
    .sort((a, b) => a.sequenceOrder - b.sequenceOrder);

/**
 * Get user-visible step events (no start/end/wait/condition) for detail page display.
 * Wait steps are internal implementation details that users don't need to see.
 */
const getVisibleStepEvents = (events = []) =>
  events
    .filter((e) => !HIDDEN_STEP_TYPES.includes((e.eventType || '').toLowerCase()))
    .sort((a, b) => a.sequenceOrder - b.sequenceOrder);

/**
 * Determine the primary step type from events (most common non-START/END event type).
 */
const getPrimaryStepType = (events = []) => {
  const actionable = getActionableEvents(events);
  if (actionable.length === 0) return null;
  return EVENT_TYPE_LABELS[actionable[0].eventType] || actionable[0].eventType;
};

/**
 * Derive an automation summary label from the cadence's events.
 * Returns one of: "Enabled for all steps", "Enabled for some steps", "No automated steps", or null.
 */
const deriveAutomationSummary = (events = []) => {
  // Only consider user-facing send steps (email, sms). Internal types like
  // wait, condition, goto, exit are always system-executed and shouldn't
  // affect the automation summary. Task steps are always manual by definition.
  const AUTOMATION_RELEVANT_TYPES = ['email', 'sms'];
  const relevant = events.filter((e) => AUTOMATION_RELEVANT_TYPES.includes((e.eventType || '').toLowerCase()));
  if (relevant.length === 0) {
    // If there are task steps but no email/sms steps, report no automated steps
    const hasTasks = events.some((e) => (e.eventType || '').toLowerCase() === 'task');
    return hasTasks ? 'No automated steps' : null;
  }
  // Use truthy check instead of strict === true, since API may return truthy non-boolean
  const automatedCount = relevant.filter((e) => !!e.isAutomated).length;
  if (automatedCount === relevant.length) return 'Enabled for all steps';
  if (automatedCount > 0) return 'Enabled for some steps';
  return 'No automated steps';
};

/**
 * Build a createdBy object from a user ID and optional user lookup map.
 * Falls back to fallbackId when the primary userId can't be resolved.
 */
const resolveUser = (userId, usersMap = {}, fallbackId) => {
  const tryResolve = (id) => {
    if (!id) return null;
    const user = usersMap[id];
    if (user && user.name && !user.removed) {
      const parts = user.name.split(' ').filter(Boolean);
      const initials =
        parts.length >= 2
          ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
          : parts[0]?.[0]?.toUpperCase() || null;
      return { id, name: user.name, initials };
    }
    return null;
  };

  return (
    tryResolve(userId) || tryResolve(fallbackId) || { id: userId || fallbackId || null, name: null, initials: null }
  );
};

/**
 * Describe a success metric in a human-readable sentence for cadence-level display.
 * Used by: Review & Publish summary, bulk message detail header, cadence detail header.
 *
 * @param {string} metricType - e.g., 'email_opened'
 * @param {number} targetPct - e.g., 50
 * @param {Object} config - metric config (interactionType, minAmount, designation, teamName, stageName, savedSearchName, etc.)
 * @returns {string}
 */
/** Format a snake_case or camelCase config value into a human-readable label. */
const humanize = (val) => {
  if (!val) return '';
  return String(val)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const describeRecipientSuccess = (metricType, config = {}) => {
  const type = (metricType || '').toLowerCase();

  switch (type) {
    case 'email_opened':
    case 'email_open_rate':
      return 'This contact opens at least one email in the cadence.';
    case 'email_replied':
    case 'email_response_rate':
      return 'This contact replies to at least one email in the cadence.';
    case 'email_clicked':
    case 'email_click_rate':
      return 'This contact clicks a link in at least one email in the cadence.';
    case 'interaction_created': {
      const typeName =
        config.interactionTypeName ||
        config.interaction_type_name ||
        humanize(config.interactionType || config.interaction_type);
      return typeName
        ? `This contact has an interaction of type "${typeName}" logged during the cadence.`
        : 'This contact has an interaction logged during the cadence.';
    }
    case 'gift_given': {
      const amt = config.minAmount || config.min_amount;
      const fmtAmt = amt ? `$${Number(amt).toLocaleString()}` : '';
      const dest = config.designation ? ` to ${config.designation}` : '';
      return fmtAmt ? `This contact makes a gift of ${fmtAmt} or more${dest}.` : `This contact makes a gift${dest}.`;
    }
    case 'gifts_amount': {
      const amt = config.minAmount ? `$${Number(config.minAmount).toLocaleString()}` : '';
      return amt
        ? `This contact gives gifts totaling ${amt} or more.`
        : 'This contact gives gifts over the target amount.';
    }
    case 'gifts_count': {
      const count = config.minCount || config.count;
      return count ? `This contact makes ${count} or more gifts.` : 'This contact reaches the target number of gifts.';
    }
    case 'meetings_count': {
      const count = config.minCount || config.count;
      return count
        ? `This contact has ${count} or more meetings logged.`
        : 'This contact reaches the target number of meetings.';
    }
    case 'portfolio_stage_reached': {
      const stage = config.stageName || config.stage_name;
      const team = config.teamName || config.team_name;
      if (stage && team) return `This contact reaches the "${stage}" stage in "${team}".`;
      if (stage) return `This contact reaches the "${stage}" portfolio stage.`;
      if (team) return `This contact reaches a stage in "${team}".`;
      return 'This contact reaches the configured portfolio stage.';
    }
    case 'saved_search': {
      const ssName = config.savedSearchName || config.saved_search_name;
      return ssName
        ? `This contact qualifies for the saved search "${ssName}".`
        : 'This contact qualifies for the configured saved search.';
    }
    case 'proposal_funded':
      return 'This contact has a proposal funded during the cadence.';
    default:
      return 'This contact meets the configured success criteria.';
  }
};

/** Derive goal label from the primary success metric (priority=0, or first). */
const deriveGoalFromMetrics = (successMetrics) => {
  if (!successMetrics?.length) return null;
  const primary = successMetrics.find((m) => m.priority === 0) || successMetrics[0];
  const key = (primary.metricType || '').toLowerCase();
  return METRIC_TYPE_LABELS[key] || primary.metricType;
};

/** Derive goal target percentage from the primary success metric. */
const deriveGoalTarget = (successMetrics) => {
  if (!successMetrics?.length) return null;
  const primary = successMetrics.find((m) => m.priority === 0) || successMetrics[0];
  return primary?.targetPercentage ?? null;
};

/** Parse a cadence template ID string to an integer, returning undefined if falsy. */
export const parseTemplateId = (value) => (value ? parseInt(value, 10) : undefined);

/**
 * Round a numerator/denominator ratio to a whole-number percentage (0-100).
 * Guards against Math.round's edge-case blindspots at the boundaries: a small
 * nonzero numerator can round down to 0%, and a numerator just short of the
 * denominator can round up to 100% — both misrepresent "some but not all" as
 * "none" or "all". Returns null when denominator is missing/non-positive.
 */
export const getRoundedPercentage = (numerator, denominator) => {
  if (!denominator || denominator <= 0) return null;
  const num = numerator ?? 0;
  let pct = Math.round((num / denominator) * 100);
  if (pct === 0 && num > 0) pct = 1;
  if (pct === 100 && num < denominator) pct = 99;
  return pct;
};

/**
 * Map an API cadence to the outreach plans list row format.
 * @param {Object} cadence - Raw cadence from API
 * @param {Object} [usersMap] - Map of userId -> { name, ... } from AllUsersStore
 */
export const mapCadenceToListRow = (cadence, usersMap, templatesMap = {}) => {
  const actionableEvents = getActionableEvents(cadence.events);
  const templateId = cadence.sourceCadenceTemplateId;

  return {
    id: cadence.id,
    title: cadence.name,
    steps: actionableEvents.length,
    stepType: getPrimaryStepType(cadence.events),
    type: CADENCE_TYPE_LABELS[cadence.cadenceType] || cadence.cadenceType,
    status:
      cadence.displayStatusLabel ||
      (cadence.status ? cadence.status.charAt(0).toUpperCase() + cadence.status.slice(1).toLowerCase() : ''),
    createdBy: resolveUser(cadence.ownerUserId, usersMap, cadence.createdByUserId),
    createdDate: cadence.createdAt || null,
    lastUpdated: cadence.updatedAt || null,
    cadenceShell: templateId ? templatesMap[templateId] || `Blueprint #${templateId}` : null,
    totalContacts: null, // Set by mergeStats(activeMembers) to avoid flash of total-enrolled
    goal: deriveGoalFromMetrics(cadence.successMetrics),
    goalMetric: null,
    goalTarget: deriveGoalTarget(cadence.successMetrics),
    automation: deriveAutomationSummary(cadence.events),
    avgDeliveryRate: null,
    avgBounceRate: null,
    avgUnsubscribeRate: null,
    avgOpenRate: null,
    avgClickRate: null,
    avgReplyRate: null,
    // Preserve raw API fields for filtering/sorting
    _raw: cadence,
  };
};

/**
 * Map an API cadence to the bulk message list row format.
 */
export const mapBulkMessageToListRow = (cadence, usersMap, templatesMap = {}) => {
  const templateId = cadence.sourceCadenceTemplateId;
  return {
    id: cadence.id,
    title: cadence.name,
    sentDate: cadence.scheduledSendTimezone
      ? formatDateInTimezone(cadence.scheduledSendDate || cadence.startedAt, cadence.scheduledSendTimezone)
      : formatDate(cadence.scheduledSendDate || cadence.startedAt),
    scheduledDate: cadence.scheduledSendTimezone
      ? formatDateInTimezone(cadence.scheduledSendDate, cadence.scheduledSendTimezone)
      : formatDate(cadence.scheduledSendDate),
    scheduledSendTimezone: cadence.scheduledSendTimezone || null,
    status:
      cadence.displayStatusLabel ||
      (cadence.status ? cadence.status.charAt(0).toUpperCase() + cadence.status.slice(1).toLowerCase() : ''),
    channel: 'Email',
    recipients: cadence.totalMembers ?? null,
    goal: deriveGoalFromMetrics(cadence.successMetrics),
    goalMetric: null,
    goalTarget: deriveGoalTarget(cadence.successMetrics),
    deliveryRate: null,
    bounceRate: null,
    unsubscribeRate: null,
    openRate: null,
    clickRate: null,
    replyRate: null,
    createdBy: resolveUser(cadence.ownerUserId, usersMap, cadence.createdByUserId),
    createdDate: cadence.createdAt || null,
    lastUpdated: cadence.updatedAt || null,
    cadenceShell: templateId ? templatesMap[templateId] || `Blueprint #${templateId}` : null,
    _raw: cadence,
  };
};

/**
 * Map an API template to the templates list row format.
 */
export const mapTemplateToListRow = (template, usersMap) => {
  const actionableEvents = getActionableEvents(template.events);
  return {
    id: template.id,
    name: template.name,
    steps: actionableEvents.length,
    stepLabel: actionableEvents.length ? `${actionableEvents.length}-steps` : 'Single Email',
    usageCount: template.templateUsageCount ?? null,
    type: CADENCE_TYPE_LABELS[template.cadenceType] || template.cadenceType,
    channel: 'Email',
    status:
      template.displayStatusLabel ||
      (template.status ? template.status.charAt(0).toUpperCase() + template.status.slice(1).toLowerCase() : ''),
    createdBy: resolveUser(template.ownerUserId, usersMap, template.createdByUserId),
    createdDate: template.createdAt || null,
    lastUpdated: template.updatedAt || null,
    totalCadences: template.templateUsageCount ?? null,
    totalContacts: template.totalMembers ?? null,
    goal: deriveGoalFromMetrics(template.successMetrics),
    goalTarget: deriveGoalTarget(template.successMetrics),
    automation: deriveAutomationSummary(template.events),
    typicalPerformance: null,
    performanceLabel: null,
    _raw: template,
  };
};

/**
 * Map API cadence events + step stats to UI step rows for the detail page.
 */
export const mapEventsToDetailSteps = (events = [], stepStats = [], edges = []) => {
  const actionable = getVisibleStepEvents(events);
  const statsMap = new Map(stepStats.map((s) => [String(s.cadenceEventId), s]));

  // Use walkCadenceGraph for branch-aware step numbering (3a, 3b)
  let stepNumberMap = {};
  try {
    const graphResult = walkCadenceGraph(events, edges);
    const mapItems = (items) => {
      (items || []).forEach((item) => {
        if (item.type === 'step' && item.event?.id) {
          stepNumberMap[String(item.event.id)] = { num: item.num, suffix: item.suffix || '' };
        }
        if (item.type === 'branch') {
          mapItems(item.trueBranch);
          mapItems(item.falseBranch);
        }
      });
    };
    mapItems(graphResult.items);
  } catch {
    // Fallback: sequential numbering
  }

  // Build a map of all events by sequenceOrder so we can look up preceding WAITs
  const allSorted = [...(events || [])].sort((a, b) => a.sequenceOrder - b.sequenceOrder);

  return actionable.map((event) => {
    const stats = statsMap.get(String(event.id));
    const config = event.eventConfig || {};
    const baseLabel = EVENT_TYPE_LABELS[event.eventType] || event.eventType || 'Step';

    // Manual tasks are sent to the backend as eventType='EMAIL' with isAutomated=false
    // because the TASK enum wasn't in the gateway schema when this was built. Detecting by
    // action_type presence is a workaround — remove once the gateway exposes TASK as a first-class
    // eventType and backfills existing records.
    const isManualTask = event.isAutomated === false && !!config.action_type;
    const isManualNonEmailTask = isManualTask && config.action_type !== 'send_email';

    const taskInfo =
      ((event.eventType || '').toUpperCase() === 'TASK' || isManualTask) && config.action_type
        ? getTaskTypeInfo(config.action_type)
        : null;
    const typeLabel = taskInfo ? taskInfo.label : baseLabel;

    // Build a descriptive name from the event config
    let name = config.name || '';
    if (!name) {
      if ((event.eventType || '').toLowerCase() === 'task' || isManualTask) {
        name = config.title || config.name || typeLabel;
      } else if ((event.eventType || '').toLowerCase() === 'email') {
        name = typeLabel;
      } else {
        name = typeLabel;
      }
    }

    // Find the preceding WAIT event (the WAIT step immediately before this action)
    const eventIdx = allSorted.findIndex((e) => e.id === event.id);
    const precedingEvent = eventIdx > 0 ? allSorted[eventIdx - 1] : null;
    const precedingWait =
      (precedingEvent?.eventType || '').toLowerCase() === 'wait'
        ? {
            days: precedingEvent.eventConfig?.duration_days || 0,
            hours: precedingEvent.eventConfig?.duration_hours || 0,
            minutes: precedingEvent.eventConfig?.duration_minutes || 0,
          }
        : null;

    const stepInfo = stepNumberMap[String(event.id)];
    // Prefer backend stepLabel from event or stats (e.g., "3a") over client-side graph walk
    const backendLabel = event.stepLabel || stats?.stepLabel || null;

    return {
      id: event.id,
      type: isManualNonEmailTask ? 'task' : (event.eventType || '').toLowerCase(),
      label: typeLabel,
      name,
      stepLabel: backendLabel,
      stepNumber: stepInfo?.num ?? null,
      stepSuffix: stepInfo?.suffix ?? '',
      isAutomated:
        (event.eventType || '').toLowerCase() === 'task' || isManualTask ? false : (event.isAutomated ?? null),
      eventConfig: config,
      emailTemplateId: event.emailTemplateId || null,
      sequenceOrder: event.sequenceOrder ?? null,
      precedingWait,
      completed: stats?.totalExecuted ?? null,
      taskCreated: stats?.tasksCreated ?? null,
      taskComplete: stats?.tasksCompleted ?? null,
      tasksSkipped: stats?.tasksSkipped ?? null,
      sends: stats?.emailsSent ?? null,
      delivered: stats?.emailsDelivered ?? null,
      opens: stats?.emailsOpened ?? null,
      clicks: stats?.emailsClicked ?? null,
      replied: stats?.emailsReplied ?? null,
      unsubscribed: stats?.emailsUnsubscribed ?? null,
      qualified: stats?.membersQualified ?? null,
      exited: stats?.membersExited ?? null,
      skipped: stats?.totalSkipped ?? 0,
      skippedReasons: stats?.skippedReasons ?? null,
      errorCount: stats?.errorCount ?? 0,
      errorReasons: stats?.errorReasons ?? null,
      cadenceEventReferenceNumber: event.cadenceEventReferenceNumber ?? null,
    };
  });
};

/**
 * Map API cadence + goal progress to detail page header format.
 */
export const mapCadenceToDetail = (cadence, goalProgress = null) => ({
  id: cadence.id,
  name: cadence.name,
  subtitle: `${getActionableEvents(cadence.events).length}-step ${getPrimaryStepType(cadence.events)?.toLowerCase() || ''} cadence`,
  goal: goalProgress
    ? {
        label: goalProgress.label,
        currentPercent: goalProgress.currentPercent,
        targetPercent: goalProgress.targetPercent,
        progressPercent: goalProgress.progressPercent,
      }
    : null,
  version: cadence.version,
  status:
    cadence.displayStatusLabel ||
    (cadence.status ? cadence.status.charAt(0).toUpperCase() + cadence.status.slice(1).toLowerCase() : ''),
  canEdit: cadence.canEdit,
  canDelete: cadence.canDelete,
  sourceCadenceTemplateId: cadence.sourceCadenceTemplateId || null,
});
